import React, {
  useCallback, useEffect, useMemo, useState, useContext,
} from 'react';
import {
  Modal, Row, Col,
  Table,
  Button,
  Badge,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Form from 'components/Form/Layout';
import Select from 'components/Form/FormSelect';
import Checkbox from 'components/Form/FormCheckbox';
import ProjectService from 'services/api/Projects';
import handlePromise from 'utils/Promise';
import classNames from 'classnames';
import { ContactType } from 'types/Invitation';
import { PromotionSummary } from 'types/Project';
import ProfileService from 'services/api/Profile';
import { LoadingContext } from 'context/Loading';
import { BsCheck } from 'react-icons/bs';

type SearchContactModalProps = {
  show: boolean,
  onClose: () => void,
  onSubmit: ( emails?: string[] ) => void,
  /** Si se pasa, no se muestra el select de promoción y se buscan contactos de esta promoción */
  promotionId?: string,
};

const CONTACT_TYPE_FILTERS: Record<ContactType, ( item: any ) => boolean> = {
  [ContactType.PRIMARY_CONTACT]: ( item ) => item.contactoPrincipal,
  [ContactType.SECONDARY_CONTACT]: ( item ) => item.contactoSecundario,
  [ContactType.INTERMEDIARY]: ( item ) => item.intermediario,
  [ContactType.LEGAL_REPRESENTATIVE]: ( item ) => item.representanteLegal,
  [ContactType.LAWYER]: ( item ) => item.abogado,
};

function StatusBadge( { value }: { value?: boolean } ) {
  if ( value === true ) return <Badge><BsCheck size={20} /></Badge>;
  if ( !value ) return <Badge bg="light"><BsCheck size={20} /></Badge>;
  return null;
}

function SearchContactModal( {
  show,
  onClose,
  onSubmit,
  promotionId,
}: SearchContactModalProps ) {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );

  const [activeProjects, setActiveProjects] = useState<PromotionSummary[]>();
  const [loading, setLoading] = useState<boolean>( false );
  const [contacts, setContacts] = useState<any[]>();
  const [filteredContacts, setFilteredContacts] = useState<any[]>();
  const [selectedContacts, setSelectedContacts] = useState<number[]>( [] );

  const contactTypes = useMemo( () => (
    Object.values( ContactType ).map( ( type ) => ( {
      id: type,
      name: t( `admin.invitations.contacts.${type}` ),
    } ) )
  ), [t] );

  const getProjects = useCallback( async () => {
    toggleLoading( true );

    const [, response, projects] = await handlePromise(
      ProjectService.getProjectsList(),
    );

    if ( response?.ok ) {
      const sortedProjects = ( projects.data as PromotionSummary[] ).sort(
        ( a, b ) => a.codigoPromocion.localeCompare( b.codigoPromocion ),
      );
      setActiveProjects( sortedProjects );
    } else {
      setActiveProjects( [] );
    }

    toggleLoading( false );
  }, [] );

  useEffect( () => {
    getProjects();
  }, [] );

  const searchContacts = useCallback(
    async ( projectId: string ) => {
      const project = activeProjects?.find(
        ( p ) => p.promocionId === projectId,
      );

      if ( !project?.cuentasInversionIds?.length ) {
        setContacts( [] );
        setFilteredContacts( [] );
        setSelectedContacts( [] );
        return;
      }

      toggleLoading( true );
      setLoading( true );

      const requests = project.cuentasInversionIds.map(
        ( cuentaDeInversionId ) => handlePromise(
          ProfileService.getAllByCuentaDeInversionBackOffice(
            { cuentaDeInversionId },
          ),
        ),
      );

      const responses = await Promise.all( requests );

      const rawContacts = responses
        .filter( ( [, response] ) => response?.ok )
        .map( ( [, , result] ) => result?.data || [] )
        .flat();

      const uniqueContactsMap = new Map(
        rawContacts.map( ( contact ) => [contact.contacId, contact] ),
      );

      const contactsResponse = Array.from( uniqueContactsMap.values() );
      setContacts( contactsResponse );
      setFilteredContacts( contactsResponse );
      setSelectedContacts(
        contactsResponse.map( ( item ) => item.contacId ),
      );

      setLoading( false );
      toggleLoading( false );
    },
    [activeProjects],
  );

  useEffect( () => {
    if ( !show || !promotionId || !activeProjects?.length ) return;
    searchContacts( promotionId );
  }, [activeProjects] );

  const searchByType = useCallback( ( selectedTypes?: ContactType[] | ContactType | null ) => {
    let typesArray: ContactType[] | null;
    if ( Array.isArray( selectedTypes ) ) {
      typesArray = selectedTypes;
    } else if ( selectedTypes ) {
      typesArray = [selectedTypes];
    } else {
      typesArray = null;
    }

    if ( !typesArray || typesArray.length === 0 ) {
      setFilteredContacts( contacts ?? [] );
      setSelectedContacts( contacts?.map( ( item ) => item.contacId ) ?? [] );
      return;
    }

    const filterFns = typesArray.map( ( type ) => CONTACT_TYPE_FILTERS[type] );
    const newContacts = contacts?.filter( ( contact ) => filterFns
      .some( ( filterFn ) => filterFn( contact ) ) );
    setFilteredContacts( newContacts ?? [] );
    setSelectedContacts( newContacts?.map( ( item ) => item.contacId ) ?? [] );
  }, [contacts] );

  const toggleContact = ( contacId: number ) => {
    setSelectedContacts( ( prev ) => ( prev.includes( contacId )
      ? prev.filter( ( id ) => id !== contacId )
      : [...prev, contacId] ) );
  };

  const toggleAll = () => {
    const allFilteredIds = filteredContacts?.map( ( c ) => c.contacId ) ?? [];
    const allSelected = allFilteredIds.every( ( id ) => selectedContacts.includes( id ) );

    if ( allSelected ) {
      setSelectedContacts( ( prev ) => prev.filter( ( id ) => !allFilteredIds.includes( id ) ) );
    } else {
      setSelectedContacts( ( prev ) => Array.from( new Set( [...prev, ...allFilteredIds] ) ) );
    }
  };

  const submitForm = useCallback( () => {
    const emails = contacts
      ?.filter( ( c ) => selectedContacts.includes( c.contacId ) )
      .map( ( c ) => c.email );

    onSubmit( emails );
  }, [selectedContacts, contacts, onSubmit] );

  const isAllSelected = useMemo( () => (
    filteredContacts?.length
    && filteredContacts.every( ( c ) => selectedContacts.includes( c.contacId ) )
  ), [filteredContacts, selectedContacts] );

  const showPromotionSelect = promotionId == null;

  return (
    <Modal
      show={show}
      centered
      onHide={onClose}
      backdrop
      fullscreen
    >
      <Modal.Header closeButton>
        <Row className="w-100">
          <Col>
            <h3 className="text-center">{t( 'admin.invitations.searchContacts' )}</h3>
          </Col>
        </Row>
      </Modal.Header>
      <Modal.Body className={classNames( {
        'app-component-loading': loading,
      } )}
      >
        <Row className="mt-3">
          <Col md={10} className="m-auto">
            <Form
              onSubmit={() => {}}
              body={( form ) => (
                <Row className="gx-1">
                  {showPromotionSelect && (
                    <Col>
                      <Select
                        options={activeProjects ?? []}
                        field="emailPromotion"
                        idKey="promocionId"
                        labelKey="codigoPromocion"
                        label="admin.invitations.promotion"
                        isLoading={!activeProjects}
                        classNames={{ formGroup: 'mb-1' }}
                        onChange={( value ) => {
                          form.change( 'contactType', undefined );
                          searchContacts( value as string );
                        }}
                      />
                    </Col>
                  )}
                  <Col>
                    <Select
                      options={contactTypes}
                      label="admin.invitations.contactType"
                      field="contactType"
                      classNames={{ formGroup: 'mb-1' }}
                      onChange={( value ) => searchByType( value as ContactType[] | null )}
                      isMulti
                    />
                  </Col>
                </Row>
              )}
            />

            <div className="mt-4">
              <Button
                size="sm"
                onClick={submitForm}
                disabled={!filteredContacts?.length}
              >
                Agregar
              </Button>
              <div className="table-responsive mt-2">
                <Table className="table--admin">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <Checkbox
                          value={!!isAllSelected}
                          onChange={toggleAll}
                          disabled={!filteredContacts?.length}
                          classNames={{
                            formGroup: 'm-0',
                          }}
                        />
                      </th>
                      <th style={{ width: 250 }}>Nombre</th>
                      <th style={{ width: 300 }}>Email</th>
                      <th>Principal</th>
                      <th>Secundario</th>
                      <th>Intermediario</th>
                      <th>Representante</th>
                      <th>Abogado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts?.map( ( item ) => (
                      <tr key={item.contacId}>
                        <td className="d-flex">
                          <Checkbox
                            value={selectedContacts.includes( item.contacId )}
                            onChange={() => toggleContact( item.contacId )}
                            classNames={{
                              formGroup: 'm-0',
                            }}
                          />
                        </td>
                        <td>
                          {`${item.firstName} ${item.lastName}`}
                        </td>
                        <td>
                          {item.email}
                        </td>
                        <td className="text-center">
                          {item.contactoPrincipal && <StatusBadge value={item.contactoPrincipal} />}
                        </td>
                        <td className="text-center">
                          {item.contactoSecundario
                           && <StatusBadge value={item.contactoSecundario} />}
                        </td>
                        <td className="text-center">
                          {item.intermediario && <StatusBadge value={item.intermediario} />}
                        </td>
                        <td className="text-center">
                          {item.representanteLegal
                          && <StatusBadge value={item.representanteLegal} />}
                        </td>
                        <td className="text-center">
                          {item.abogado
                          && <StatusBadge value={item.abogado} />}
                        </td>
                      </tr>
                    ) )}

                    {!filteredContacts?.length && (
                      <tr>
                        <td colSpan={8} className="text-center">No hay contactos para agregar</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default SearchContactModal;
